/**
 * Legacy origin for pickleball.com images. Exported because several callers build
 * placeholder/static asset URLs from it before handing them to `getCDNURL`.
 */

import { NEXT_PUBLIC_IMAGE_CDN_URL } from './constants';

const NEW_NEXT_PUBLIC_IMAGE_CDN = 'https://cdn.pickleball.com';

const NEXT_PUBLIC_PBRACKETS_IMAGE_CDN = 'https://pickleballbrackets.com';
const NEW_NEXT_PUBLIC_PBRACKETS_IMAGE_CDN = 'https://cdn.pickleballbrackets.com';

interface CDNHostPair {
  /** Origin the asset is stored under before the CDN rewrite. */
  legacy: string;
  /** CDN origin the URL is rewritten to, and where the resize params are honoured. */
  cdn: string;
  /**
   * Whether this CDN understands `saturation` and `face_crop`.
   *
   * The pickleballbrackets CDN does not — emitting them there produces a URL
   * carrying params the CDN ignores, which makes the cache key noisier for no
   * benefit. They are dropped for those hosts instead of being passed through.
   */
  supportsEnhancements: boolean;
}

/**
 * Host pairs this helper manages. First match wins.
 *
 * Order is not significant today (neither legacy origin is a prefix of the
 * other), but if a subdomain pair is ever added, list the more specific origin
 * first — matching is `startsWith`, not hostname equality.
 */
const CDN_HOSTS: readonly CDNHostPair[] = [
  {
    legacy: NEXT_PUBLIC_IMAGE_CDN_URL,
    cdn: NEW_NEXT_PUBLIC_IMAGE_CDN,
    supportsEnhancements: true,
  },
  {
    legacy: NEXT_PUBLIC_PBRACKETS_IMAGE_CDN,
    cdn: NEW_NEXT_PUBLIC_PBRACKETS_IMAGE_CDN,
    supportsEnhancements: false,
  },
];

/**
 * Rewrites an image URL onto its CDN host and appends the resize query the CDN expects.
 *
 * This used to be two functions: `getCDNURL` for `images.pickleball.com` assets and
 * `getPBracketsCDNURL` (in `packages/ui`) for `pickleballbrackets.com` assets. Same
 * algorithm, different host pair. Because each one returned the URL untouched when it
 * did not recognise the host, calling the wrong one was a silent no-op — the image
 * simply came back unresized. 21 of the 26 files that imported the brackets helper
 * also imported this one purely to branch between the two, and the branch condition
 * (`isAbsoluteUrl`, `logo.includes('/uploads/')`, `Logo.includes('placeholder')`) was
 * hand-rolled differently in almost every one.
 *
 * The host pair is now resolved from `CDN_HOSTS` rather than hardcoded, so a caller
 * passes any image URL and gets the correct CDN without knowing which one it is.
 *
 * A URL on a host we do not manage is returned untouched. That is the contract every
 * call site depends on for placeholders, external/partner logos and media-service
 * URLs — do not make this throw or fall back to a default host.
 *
 * Both host pairs are PROD origins on every environment: DEV and TEST also serve
 * images from `images.pickleball.com` / `cdn.pickleball.com`, so the origins are
 * deliberately hardcoded here instead of read from `NEXT_PUBLIC_IMAGE_CDN_URL`.
 *
 * @param url Any image URL. Returned as-is when empty or on an unmanaged host.
 * @param width Resize width. Resize params are only added when width AND height are truthy.
 * @param height Resize height. See `width`.
 * @param saturation Passed through as `saturation`; `0` renders the image greyscale.
 *   Silently dropped for hosts whose CDN does not support it (see `CDNHostPair`).
 * @param faceCrop Adds `face_crop=true`, which crops around the detected face — used
 *   for player profile images. Silently dropped for hosts whose CDN does not support it.
 *
 * @returns The CDN URL with resize params, or `url` unchanged when it is empty or not
 *   on a managed host.
 */
export function getCDNURL(
  url: string,
  width: number,
  height: number,
  saturation?: number,
  faceCrop?: boolean
): string {
  if (!url) return url;

  // 1. Find the host pair this URL belongs to. A URL already on the CDN origin still
  //    matches — callers re-run this helper on already-rewritten URLs (e.g. a value
  //    that went through an API mapper and is resized again at the `<img src>` site),
  //    and it needs to pick up the new dimensions rather than bail out.
  const host = CDN_HOSTS.find((entry) => url.startsWith(entry.legacy) || url.startsWith(entry.cdn));

  // 2. Not ours — placeholders, partner logos, media-service URLs. Leave it alone.
  if (!host) return url;

  const normalizedUrl = url.startsWith(host.cdn) ? url : url.replace(host.legacy, host.cdn);

  const parsed = new URL(normalizedUrl);

  // 3. Resizing is opt-in: callers pass 0/undefined dimensions when they only want the
  //    host rewritten (the CDN serves the original at full size in that case).
  if (width && height) {
    parsed.searchParams.set('width', String(width));
    parsed.searchParams.set('height', String(height));
    parsed.searchParams.set('optimizer', 'image');

    if (host.supportsEnhancements) {
      if (typeof saturation === 'number') {
        parsed.searchParams.set('saturation', String(saturation));
      }

      if (faceCrop) {
        parsed.searchParams.set('face_crop', 'true');
      }
    }
  }

  return parsed.toString();
}
